import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const SimpleKanban = ({ tickets, onDragEnd, getPriorityBadge, handleTicketClick }) => {
  // Função para lidar com erros de drag-and-drop
  const handleDragEnd = (result) => {
    try {
      onDragEnd(result);
    } catch (error) {
      console.error('Erro no drag-and-drop:', error);
    }
  };
  return (
    <DragDropContext onDragEnd={handleDragEnd} combine={false}>
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 min-h-[600px]">
        {/* Column 1: Novos/Abertos */}
        <div className="bg-gradient-to-b from-slate-50 to-slate-100 rounded-xl p-4 border-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-700 flex items-center">
              <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
              Novos
            </h4>
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'aberto' || t.status === 'novo').length}
            </span>
          </div>
          <Droppable droppableId="novos" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'aberto' || t.status === 'novo').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-all cursor-move"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                            </div>
                            <h5 className="font-semibold text-slate-900 text-sm line-clamp-2">{ticket.title}</h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{ticket.description}</p>
                          </div>
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        
                        <div className="flex justify-between items-center text-xs text-slate-500">
                          <span className="bg-slate-100 px-2 py-1 rounded">{ticket.ticket_type}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        
                        {ticket.product_id && (
                          <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            📦 Qtd: {ticket.requested_quantity}
                          </div>
                        )}

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Column 2: Em Andamento */}
        <div className="bg-gradient-to-b from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-purple-700 flex items-center">
              <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
              Em Andamento
            </h4>
            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'em_andamento' || t.status === 'aguardando_aprovacao').length}
            </span>
          </div>
          <Droppable droppableId="andamento" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'em_andamento' || t.status === 'aguardando_aprovacao').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-purple-200 hover:shadow-md transition-all cursor-move"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                            </div>
                            <h5 className="font-semibold text-slate-900 text-sm line-clamp-2">{ticket.title}</h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{ticket.description}</p>
                          </div>
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        
                        <div className="flex justify-between items-center text-xs text-slate-500">
                          <span className="bg-purple-100 px-2 py-1 rounded">{ticket.ticket_type}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        
                        <div className="mt-2 flex items-center text-xs">
                          <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                          <span className="text-purple-700">⚙️ Sendo processado...</span>
                        </div>

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Column 3: Em Aprovação */}
        <div className="bg-gradient-to-b from-yellow-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-orange-700 flex items-center">
              <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
              Em Aprovação
            </h4>
            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'em_aprovacao' || t.status === 'aguardando_aprovacao').length}
            </span>
          </div>
          <Droppable droppableId="aprovacao" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'em_aprovacao' || t.status === 'aguardando_aprovacao').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-orange-200 hover:shadow-md transition-all cursor-move"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                              {getPriorityBadge && getPriorityBadge(ticket.priority)}
                            </div>
                            <h5 className="font-medium text-slate-900 text-sm leading-tight">
                              {ticket.title}
                            </h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                              {ticket.description}
                            </p>
                          </div>
                        </div>

                        <div className="text-xs text-orange-600 mb-2">
                          <span className="text-orange-700">⏳ Aguardando aprovação...</span>
                        </div>

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Column 4: Aprovados */}
        <div className="bg-gradient-to-b from-emerald-50 to-emerald-100 rounded-xl p-4 border-2 border-emerald-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-emerald-700 flex items-center">
              <span className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></span>
              Aprovados
            </h4>
            <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'aprovado').length}
            </span>
          </div>
          <Droppable droppableId="aprovados" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'aprovado').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-emerald-200 hover:shadow-md transition-all cursor-move"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                              {getPriorityBadge && getPriorityBadge(ticket.priority)}
                            </div>
                            <h5 className="font-medium text-slate-900 text-sm leading-tight">
                              {ticket.title}
                            </h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                              {ticket.description}
                            </p>
                          </div>
                        </div>

                        <div className="text-xs text-emerald-600 mb-2">
                          <span className="text-emerald-700">✅ Aprovado - Pronto para resolução</span>
                        </div>

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Column 5: Resolvidos */}
        <div className="bg-gradient-to-b from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-green-700 flex items-center">
              <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
              Resolvidos
            </h4>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'resolvido' || t.status === 'concluido').length}
            </span>
          </div>
          <Droppable droppableId="resolvidos" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'resolvido' || t.status === 'concluido').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-green-200 hover:shadow-md transition-all cursor-move"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                            </div>
                            <h5 className="font-semibold text-slate-900 text-sm line-clamp-2">{ticket.title}</h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{ticket.description}</p>
                          </div>
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        
                        <div className="flex justify-between items-center text-xs text-slate-500">
                          <span className="bg-green-100 px-2 py-1 rounded">{ticket.ticket_type}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        
                        <div className="mt-2 flex items-center text-xs">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          <span className="text-green-700">✅ Aguardando aprovação</span>
                        </div>

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Column 6: Fechados */}
        <div className="bg-gradient-to-b from-slate-50 to-slate-100 rounded-xl p-4 border-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-700 flex items-center">
              <span className="w-3 h-3 bg-slate-500 rounded-full mr-2"></span>
              Fechados
            </h4>
            <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded-full text-xs font-medium">
              {tickets.filter(t => t.status === 'fechado').length}
            </span>
          </div>
          <Droppable droppableId="fechados" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
            {(provided) => (
              <div 
                className="space-y-3 min-h-[400px]"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {tickets.filter(t => t.status === 'fechado').map((ticket, index) => (
                  <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                    {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-all cursor-move opacity-75"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                {ticket.ticket_number || `#${ticket.id.substring(0, 8)}`}
                              </span>
                            </div>
                            <h5 className="font-semibold text-slate-900 text-sm line-clamp-2">{ticket.title}</h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{ticket.description}</p>
                          </div>
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        
                        <div className="flex justify-between items-center text-xs text-slate-500">
                          <span className="bg-slate-100 px-2 py-1 rounded">{ticket.ticket_type}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        
                        <div className="mt-2 flex items-center text-xs">
                          <span className="w-2 h-2 bg-slate-500 rounded-full mr-2"></span>
                          <span className="text-slate-600">🎯 Concluído</span>
                        </div>

                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Arraste para mover</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTicketClick(ticket);
                            }}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            👁️ Ver
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragDropContext>
  );
};

export default SimpleKanban;